<script setup>
import { ref, computed, nextTick, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useBranding } from 'shared/composables/useBranding';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import WebhookForm from './WebhookForm.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import EditSheet from 'dashboard/components-next/polaris/cards/EditSheet.vue';

// Phase 9.9 (R2-9-5: webhook = drawer): the new webhook in the kit's EditSheet; after it is created the same
// drawer shows its secret, and Save becomes "Done".

const props = defineProps({
  onClose: {
    type: Function,
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();
const { replaceInstallationName } = useBranding();

const createdWebhook = ref(null);
const formRef = ref(null);
const sheetRef = ref(null);
const snapshot = ref('');
onMounted(() =>
  nextTick(() => {
    snapshot.value = formRef.value?.formJSON ?? '';
  })
);
const dirty = computed(
  () => !createdWebhook.value && formRef.value?.formJSON !== snapshot.value
);
const scope = computed(
  () => `${t('SIDEBAR.SETTINGS')} · ${t('INTEGRATION_SETTINGS.WEBHOOK.TITLE')}`
);
const onSave = () => {
  if (createdWebhook.value) props.onClose();
  else formRef.value?.onSubmit();
};
const runGuarded = action =>
  sheetRef.value ? sheetRef.value.runGuarded(action) : action();
defineExpose({ runGuarded });

const uiFlags = computed(() => store.getters['webhooks/getUIFlags']);

const onSubmit = async webhook => {
  try {
    const result = await store.dispatch('webhooks/create', { webhook });
    createdWebhook.value = result;
  } catch (error) {
    const message =
      error.response.data.message ||
      t('INTEGRATION_SETTINGS.WEBHOOK.EDIT.API.ERROR_MESSAGE');
    useAlert(message);
  }
};

const handleCopySecret = async () => {
  await copyTextToClipboard(createdWebhook.value.secret);
  useAlert(t('INTEGRATION_SETTINGS.WEBHOOK.SECRET.COPY_SUCCESS'));
};
</script>

<template>
  <EditSheet
    ref="sheetRef"
    open
    :title="
      createdWebhook
        ? t('INTEGRATION_SETTINGS.WEBHOOK.ADD.API.SUCCESS_MESSAGE')
        : t('INTEGRATION_SETTINGS.WEBHOOK.ADD.TITLE')
    "
    :scope="scope"
    :dirty="dirty"
    :save-label="
      createdWebhook
        ? t('INTEGRATION_SETTINGS.WEBHOOK.SECRET.DONE')
        : t('INTEGRATION_SETTINGS.WEBHOOK.FORM.ADD_SUBMIT')
    "
    :can-save="
      !!createdWebhook || (!formRef?.isInvalid && !uiFlags.creatingItem)
    "
    @update:open="value => !value && props.onClose()"
    @save="onSave"
  >
    <template v-if="createdWebhook">
      <p class="type-polaris-body text-polaris-muted-foreground">
        {{ t('INTEGRATION_SETTINGS.WEBHOOK.SECRET.CREATED_DESC') }}
      </p>
      <label class="grid gap-2 type-polaris-label">
        {{ t('INTEGRATION_SETTINGS.WEBHOOK.SECRET.LABEL') }}
        <span class="flex items-center gap-2">
          <input
            :value="createdWebhook.secret"
            type="text"
            readonly
            class="!mb-0 font-mono"
          />
          <NextButton
            v-tooltip.top="t('INTEGRATION_SETTINGS.WEBHOOK.SECRET.COPY')"
            icon="i-lucide-copy"
            slate
            faded
            @click="handleCopySecret"
          />
        </span>
      </label>
    </template>
    <template v-else>
      <p class="type-polaris-body text-polaris-muted-foreground">
        {{
          replaceInstallationName(t('INTEGRATION_SETTINGS.WEBHOOK.FORM.DESC'))
        }}
      </p>
      <WebhookForm ref="formRef" @submit="onSubmit" />
    </template>
  </EditSheet>
</template>
